import React, { useState, useRef } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Upload, FileText, Copy, Check } from 'lucide-react';

export default function BulkImport() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/employees/bulk-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
      toast.success(`Imported ${res.data.imported} employees`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const copyCredentials = (emp) => {
    const text = `Employee: ${emp.name}\nEmail: ${emp.email}\nCode: ${emp.employee_code}\nPIN: ${emp.pin}`;
    navigator.clipboard.writeText(text);
    setCopiedId(emp.employee_code);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Credentials copied!');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bulk-import-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800">Bulk Import</h1>
        <p className="text-slate-500">Import employees from a CSV/text file</p>
      </div>

      <Card className="bg-white border shadow-sm">
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-2">File Format (CSV - one employee per line):</p>
              <code className="text-xs bg-white p-2 rounded block">
                Full Name, email@example.com, Phone Number<br/>
                Raj Kumar, raj@seeza.com, 9876543210<br/>
                Priya Sharma, priya@seeza.com, 9876543211
              </code>
            </div>
            <div className="flex gap-3 items-center">
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.txt,.pdf"
                onChange={(e) => setFile(e.target.files[0])}
                className="hidden"
                data-testid="bulk-file-input"
              />
              <Button variant="outline" onClick={() => fileRef.current?.click()} className="flex-1" data-testid="select-file-btn">
                <FileText className="w-4 h-4 mr-2" />
                {file ? file.name : 'Select File'}
              </Button>
              <Button onClick={handleUpload} disabled={!file || loading} className="bg-[#FF9933] hover:bg-[#E68A2E]" data-testid="import-btn">
                <Upload className="w-4 h-4 mr-2" />
                {loading ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <>
          <div className="flex gap-4">
            <Card className="flex-1 bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{result.imported}</p>
                <p className="text-sm text-green-700">Imported</p>
              </CardContent>
            </Card>
            {result.errors > 0 && (
              <Card className="flex-1 bg-red-50 border-red-200">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{result.errors}</p>
                  <p className="text-sm text-red-700">Errors</p>
                </CardContent>
              </Card>
            )}
          </div>

          {result.employees?.length > 0 && (
            <Card className="bg-white border shadow-sm">
              <CardHeader><CardTitle>Imported Employees - Copy Login Details</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table data-testid="import-result-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Employee Code</TableHead>
                      <TableHead>PIN</TableHead>
                      <TableHead className="text-right">Copy</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.employees.map((emp) => (
                      <TableRow key={emp.employee_code}>
                        <TableCell className="font-medium">{emp.name}</TableCell>
                        <TableCell>{emp.email}</TableCell>
                        <TableCell className="font-mono font-bold text-[#FF9933]">{emp.employee_code}</TableCell>
                        <TableCell className="font-mono">{emp.pin}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => copyCredentials(emp)} data-testid={`copy-cred-${emp.employee_code}`}>
                            {copiedId === emp.employee_code ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {result.error_details?.length > 0 && (
            <Card className="bg-white border border-red-200 shadow-sm">
              <CardHeader><CardTitle className="text-red-600">Import Errors</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1 text-sm">
                  {result.error_details.map((err, i) => (
                    <p key={i} className="text-red-600">Line {err.line}: {err.error}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
